import { gsap } from "gsap";
import { lerpfi } from "../../utils/lerp.js";
import { observeMediaElement } from "../../utils/mediaTelemetry";
import { loadBgVideo } from "../../utils/bgVideo";
import { mediaLoadEvent } from "../../utils/videoKind";

const SMOOTHING = 0.08;

export default class CategoriesPush {
  constructor(el) {
    this.el = el;
    this.init();
  }

  init() {
    this.items = [...this.el.querySelectorAll(".c-categories-push__item")];
    this.hovers = [...this.el.querySelectorAll(".c-categories-push__hover")];

    if (this.el.querySelector("media-player")) {
      this.vidstackReady = import("vidstack/player").then(() =>
        customElements.whenDefined("media-player"),
      );
    }

    this.items.forEach((item, index) => {
      const hover = this.hovers[index];
      if (!hover) return;
      // `media` is the wrapper (scaled in/out); `player` is the actual
      // <video> / <media-player> / image div inside it.
      const media = hover.querySelector(".c-categories-push__hover__media");
      const player = hover.querySelector(".c-categories-push__hover__el");
      const text = hover.querySelector(".c-categories-push__hover__text");

      const isPlayer = player?.tagName === "MEDIA-PLAYER";
      const isVideo = isPlayer || player?.tagName === "VIDEO";
      let hovering = false;

      if (isVideo) {
        player.addEventListener(
          mediaLoadEvent(player),
          () => media.classList.add("is-ready"),
          { once: true },
        );
        observeMediaElement(player, {
          context: "categories-push",
          kind: "hover",
          src: player.getAttribute("src") || player.dataset.src,
        });
      }

      const playVideo = () => {
        if (!isVideo) return;
        hovering = true;
        const attempt = () => {
          if (!hovering) return;
          loadBgVideo(player);
          const p = player.play?.();
          if (p && p.catch) p.catch(() => {});
        };
        if (isPlayer) this.vidstackReady?.then(attempt);
        else attempt();
      };

      const pauseVideo = () => {
        if (!isVideo) return;
        hovering = false;
        player.pause?.();
      };

      gsap.set(media, { scale: 0 });
      text && gsap.set(text, { scale: 0, y: "-100%" });

      let currentX = 0, currentY = 0;
      let targetX = 0, targetY = 0;

      const tick = () => {
        currentX = lerpfi(currentX, targetX, SMOOTHING, gsap.ticker.deltaMille);
        currentY = lerpfi(currentY, targetY, SMOOTHING, gsap.ticker.deltaMille);
        gsap.set(hover, { x: currentX, y: currentY });
      };

      item.addEventListener("mouseenter", (e) => {
        const rect = this.el.getBoundingClientRect();
        currentX = targetX = e.clientX - rect.left;
        currentY = targetY = e.clientY - rect.top;
        gsap.set(hover, { x: currentX, y: currentY });

        gsap.killTweensOf(this.hovers);
        gsap.set(
          this.hovers.filter((h) => h !== hover),
          { autoAlpha: 0 },
        );
        gsap.to(hover, { autoAlpha: 1, duration: 0.3, ease: "power2.out" });
        gsap.fromTo(media, {scale: 0}, { scale: 1, duration: 0.6, ease: "expo.out" });
        playVideo();
        text && gsap.fromTo(text, {scale: 0, y: "-100%"}, { delay: 0.05, scale: 1, y: "0%", duration: 0.6, ease: "expo.out" });
        gsap.ticker.add(tick);
      });

      item.addEventListener("mouseleave", () => {
        gsap.to(hover, { autoAlpha: 0, duration: 0.3, ease: "power2.out" });
        gsap.to(media, { scale: 0, duration: 0.3, ease: "power2.in" });
        pauseVideo();
        text && gsap.to(text, { scale: 0, y: "-100%", duration: 0.3, ease: "power2.in" });
        gsap.ticker.remove(tick);
      });

      item.addEventListener("mousemove", (e) => {
        const rect = this.el.getBoundingClientRect();
        targetX = e.clientX - rect.left;
        targetY = e.clientY - rect.top;
      });
    });
  }
}
